import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2, Upload, X, Loader2, Lock, ShieldCheck, ShieldX, AlertTriangle, CheckCircle2, RotateCcw } from "lucide-react";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const DOC_SLOTS = [
  { key: "original_po",        label: "Original Purchase Order",   accept: "application/pdf,image/*" },
  { key: "signed_pod",         label: "Signed POD · Lorry Receipt",accept: "application/pdf,image/*" },
  { key: "supplier_invoice",   label: "Supplier Invoice",          accept: "application/pdf,image/*" },
  { key: "payment_receipt",    label: "Payment Receipt",           accept: "application/pdf,image/*" },
  { key: "discrepancy_report", label: "Signed Discrepancy Report", accept: "application/pdf,image/*" },
];

export function OrderClaimButton({ api, order, onSubmitted }) {
  const [elig, setElig] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get(`/orders/${order.id}/claim-eligibility`);
      setElig(r.data);
      if (r.data.recovery_path) {
        try {
          const pf = await api.get(`/orders/${order.id}/claim-prefill`);
          setPrefill(pf.data);
        } catch { /* noop */ }
      }
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [order.id]);
  if (!elig) return null;

  const btn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.18em] transition-colors";

  // Show status pill for an active (non-rejected) claim
  if (elig.already_claimed) return (
    <span data-testid={`claim-status-${order.id}`} className={`${btn} border border-[#3DBB84]/40 text-[#3DBB84]`}>
      <ShieldCheck size={11}/> Claim · {elig.existing_claim?.status?.replace(/_/g, " ")}
    </span>
  );
  // Recovery Path — prior rejected, open a resubmit button
  if (elig.recovery_path) {
    if (!elig.eligible) {
      return (
        <span className={`${btn} border border-[#8A8A82]/40 text-[#8A8A82] cursor-not-allowed`}
              title="Rejection grace window closed or gates not met">
          <ShieldX size={11}/> Recovery closed
        </span>
      );
    }
    return (
      <>
        <button data-testid={`resubmit-claim-${order.id}`} onClick={() => setOpen(true)}
                className={`${btn} bg-[#B87333] text-white hover:bg-[#a26529]`}>
          <RotateCcw size={11}/> Resubmit after rejection
        </button>
        <AnimatePresence>
          {open && <ClaimModal elig={elig} prefill={prefill} loading={loading}
                                onClose={() => setOpen(false)} onSubmit={submit}/>}
        </AnimatePresence>
      </>
    );
  }
  if (!elig.eligible) return (
    <span className={`${btn} border border-[#8A8A82]/40 text-[#8A8A82] cursor-not-allowed`} title="Compliance gates not met">
      <ShieldX size={11}/> Claim locked
    </span>
  );

  async function submit(payload) {
    setLoading(true);
    try {
      await api.post(`/orders/${order.id}/claim`, payload);
      toast.success(prefill?.has_prior
        ? "Resubmitted — Vinci Ops will re-review the flagged document."
        : "Iron-clad claim submitted — Vinci Ops has been notified.");
      setOpen(false); onSubmitted && onSubmitted(); refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compliance violation");
    } finally { setLoading(false); }
  }

  return (
    <>
      <button data-testid={`initiate-claim-${order.id}`} onClick={() => setOpen(true)}
              className={`${btn} bg-[#D8B23E] text-black hover:bg-[#c5a233]`}>
        <FileCheck2 size={11}/> Initiate Commission Claim
      </button>
      <AnimatePresence>
        {open && <ClaimModal elig={elig} prefill={null} loading={loading}
                              onClose={() => setOpen(false)} onSubmit={submit}/>}
      </AnimatePresence>
    </>
  );
}

function ClaimModal({ elig, prefill, loading, onClose, onSubmit }) {
  const priorLines = prefill?.line_items || [];
  const priorTicks = Object.fromEntries(priorLines.map(li => [li.item_id, true]));
  const [invoiceNumber, setInvoiceNumber] = useState(prefill?.invoice_number || "");
  const [amountPaid, setAmountPaid] = useState(String(prefill?.amount_paid ?? Math.round(elig.total_inr || 0)));
  const [amountClaimed, setAmountClaimed] = useState(String(prefill?.amount_claimed ?? Math.round(elig.calculated_commission_inr || 0)));
  const [payoutDetails, setPayoutDetails] = useState(prefill?.payout_details || "");
  const [remarks, setRemarks] = useState(prefill?.remarks || "");
  const [lineTicks, setLineTicks] = useState(() =>
    Object.keys(priorTicks).length
      ? priorTicks
      : Object.fromEntries((elig.line_items||[]).map(li => [li.item_id, true]))
  );
  const [files, setFiles] = useState({});

  const setFile = (k, f) => setFiles(p => ({ ...p, [k]: f }));

  const asB64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res((r.result || "").toString().split(",")[1] || "");
    r.onerror = rej; r.readAsDataURL(f);
  });

  // Live compliance gates (mirrors backend rules)
  const allDocs = DOC_SLOTS.every(d => files[d.key]);
  const someLine = Object.values(lineTicks).some(Boolean);
  const formOk = invoiceNumber.trim() && amountPaid && amountClaimed && payoutDetails.trim();
  const gates = [
    { key: "fulfilled",       ok: elig.gates.fulfilled,       label: "Order marked DELIVERED" },
    { key: "paid",            ok: elig.gates.paid,            label: "Payment marked PAID" },
    { key: "single_claim",    ok: elig.gates.single_claim,    label: prefill?.has_prior ? "Resubmission accepted (prior claim rejected)" : "No prior claim on this PO" },
    { key: "within_30_days",  ok: elig.gates.within_30_days,  label: elig.grace_window_active
        ? `Recovery grace active · ${elig.grace_deadline_at ? new Date(elig.grace_deadline_at).toLocaleDateString("en-IN", {day:"2-digit", month:"short"}) : "7 days"} deadline`
        : `Within ${elig.sla_days} days of delivery${elig.days_since_delivery!=null ? ` · day ${elig.days_since_delivery}` : ""}` },
    { key: "line_items",      ok: someLine,                    label: "At least one delivered line-item confirmed" },
    { key: "documents",       ok: allDocs,                     label: `All ${DOC_SLOTS.length} mandatory documents attached` },
    { key: "form",            ok: !!formOk,                    label: "Financials + payout details filled" },
  ];
  const allGreen = gates.every(g => g.ok);

  const submit = async () => {
    if (!allGreen || loading) return;
    const docs = {};
    for (const s of DOC_SLOTS) {
      const f = files[s.key];
      docs[s.key] = { content: await asB64(f), filename: f.name, content_type: f.type || "application/pdf" };
    }
    const picked = (elig.line_items || []).filter(li => lineTicks[li.item_id]).map(li => ({
      item_id: li.item_id, name: li.name, grade: li.grade,
      quantity_claimed: li.quantity_delivered,
    }));
    onSubmit({
      order_id: elig.order_id,
      invoice_number: invoiceNumber.trim(),
      amount_paid: Number(amountPaid),
      amount_claimed: Number(amountClaimed),
      payout_details: payoutDetails.trim(),
      remarks: remarks.trim(),
      line_items: picked,
      ...docs,
    });
  };

  const Slot = ({ slot }) => {
    const priorMeta = prefill?.documents_meta?.[slot.key];
    return (
      <div className={`border rounded-sm bg-black/30 p-3 ${files[slot.key] ? "border-[#3DBB84]/40" : (priorMeta && !files[slot.key] ? "border-[#B87333]/40" : "border-white/10")}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
            {slot.label} <span className="text-[#E5484D]">*</span>
          </span>
          {files[slot.key] && <span className="font-body text-[10px] text-[#3DBB84]">✓ {files[slot.key].name.slice(0,26)}</span>}
          {!files[slot.key] && priorMeta?.filename && (
            <span className="font-body text-[10px] text-[#B87333]" title="Prior upload was archived. Re-upload only if the reviewer flagged this file.">
              ↺ prior: {priorMeta.filename.slice(0,22)}
            </span>
          )}
        </div>
        <label className="cursor-pointer inline-flex items-center gap-2 rounded-sm border border-white/15 hover:border-[#D8B23E] px-3 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Upload size={11}/> {files[slot.key] ? "Replace" : (priorMeta ? "Re-upload" : "Upload")}
          <input data-testid={`claim-upload-${slot.key}`} type="file" accept={slot.accept}
                 onChange={e => setFile(slot.key, e.target.files?.[0] || null)} className="hidden"/>
        </label>
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="bg-[#050506] w-full max-w-4xl border border-[#D8B23E]/40 rounded-sm overflow-hidden"
        data-testid="commission-claim-modal">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
              {prefill?.has_prior ? "Recovery Path · Resubmit" : "Iron-Clad Claims Engine"}
            </div>
            <div className="font-serif text-xl text-[#F5F5F0] mt-1 inline-flex items-center gap-2">
              <Lock size={14} className="text-[#D8B23E]"/> PO Locked · <span className="font-mono">{elig.po_number}</span>
            </div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1">
              {elig.material} · Order value {inr(elig.total_inr)} · Delivered day {elig.days_since_delivery ?? "—"} of {elig.sla_days}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {elig.compliance && (
              <div className="text-right">
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#D8B23E]">Your Compliance</div>
                <div className="font-serif text-lg text-[#F5F5F0]">{elig.compliance.score}<span className="text-[#8A8A82] text-xs"> / 100</span></div>
                <div className="font-body text-[10px] text-[#8A8A82]">{elig.compliance.tier?.label}</div>
              </div>
            )}
            <button data-testid="claim-modal-close" onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20}/></button>
          </div>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {prefill?.has_prior && prefill?.rejection_reason && (
            <div data-testid="prior-rejection-notice" className="border border-[#E5484D]/40 bg-[#E5484D]/[0.05] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-[#E5484D]"/>
                <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#E5484D]">
                  Prior rejection · Fix and resubmit
                </span>
              </div>
              <div className="font-body text-[12px] text-[#F5F5F0] italic">&ldquo;{prefill.rejection_reason}&rdquo;</div>
              <div className="font-body text-[10px] text-[#8A8A82] mt-1">
                Rejected on {new Date(prefill.rejected_at).toLocaleDateString("en-IN", {day:"2-digit", month:"short", year:"numeric"})}
                {elig.grace_deadline_at && (
                  <> · Recovery deadline {new Date(elig.grace_deadline_at).toLocaleDateString("en-IN", {day:"2-digit", month:"short"})}</>
                )}
              </div>
            </div>
          )}

          {/* Live compliance panel */}
          <div className={`border rounded-sm p-4 ${allGreen ? "border-[#3DBB84]/40 bg-[#3DBB84]/[0.05]" : "border-[#E5484D]/40 bg-[#E5484D]/[0.05]"}`}>
            <div className="flex items-center gap-2 mb-3">
              {allGreen ? <CheckCircle2 size={14} className="text-[#3DBB84]"/> : <AlertTriangle size={14} className="text-[#E5484D]"/>}
              <span className={`font-grotesk text-[10px] uppercase tracking-[0.28em] ${allGreen ? "text-[#3DBB84]" : "text-[#E5484D]"}`}>
                {allGreen ? "Compliant · Ready to submit" : "Compliance Violation · fix red rows below"}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {gates.map(g => (
                <div key={g.key} data-testid={`claim-gate-${g.key}`}
                     className={`flex items-center gap-2 text-[11px] font-body ${g.ok ? "text-[#3DBB84]" : "text-[#E5484D]"}`}>
                  {g.ok ? <CheckCircle2 size={11}/> : <ShieldX size={11}/>} {g.label}
                </div>
              ))}
            </div>
          </div>

          {/* Line-item sync */}
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">
              Line items · confirm what was delivered
            </div>
            <div className="grid gap-2">
              {(elig.line_items || []).map(li => (
                <label key={li.item_id} className="flex items-center gap-3 border border-white/10 rounded-sm p-3 cursor-pointer hover:border-white/20">
                  <input data-testid={`claim-line-${li.item_id}`} type="checkbox" checked={!!lineTicks[li.item_id]}
                         onChange={e => setLineTicks(p => ({...p, [li.item_id]: e.target.checked}))}/>
                  <div className="flex-1">
                    <div className="font-body text-sm text-[#F5F5F0]">{li.name} <span className="text-[#8A8A82]">· {li.grade || "—"}</span></div>
                    <div className="font-body text-[11px] text-[#8A8A82]">Qty delivered: <span className="text-[#F5F5F0]">{li.quantity_delivered}</span> · Line total {inr(li.amount)}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><div className="font-body text-[11px] text-[#8A8A82] mb-1">Invoice number *</div>
              <input data-testid="claim-invoice-number" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)}
                     placeholder="INV/2026/0001" className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/></div>
            <div><div className="font-body text-[11px] text-[#8A8A82] mb-1">Amount paid (INR) *</div>
              <input data-testid="claim-amount-paid" type="number" value={amountPaid} onChange={e => setAmountPaid(e.target.value)}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/></div>
            <div><div className="font-body text-[11px] text-[#8A8A82] mb-1">Calculated commission * <span className="text-[#6f6f68]">· hint: {inr(elig.calculated_commission_inr)}</span></div>
              <input data-testid="claim-amount-claimed" type="number" value={amountClaimed} onChange={e => setAmountClaimed(e.target.value)}
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/></div>
            <div className="sm:col-span-2"><div className="font-body text-[11px] text-[#8A8A82] mb-1">Payout details (bank / UPI) *</div>
              <input data-testid="claim-payout" value={payoutDetails} onChange={e => setPayoutDetails(e.target.value)}
                     placeholder="A/C 000123456789 · IFSC HDFC0000123 · UPI xxx@ok"
                     className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/></div>
            <div className="sm:col-span-2"><div className="font-body text-[11px] text-[#8A8A82] mb-1">Remarks (optional)</div>
              <textarea data-testid="claim-remarks" rows={2} value={remarks} onChange={e => setRemarks(e.target.value)}
                        className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/></div>
          </div>

          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mb-2">
              Mandatory Vault · all 5 uploads required
              {prefill?.has_prior && (
                <span className="ml-2 text-[#B87333]">· prior uploads shown in copper — re-upload the flagged file(s)</span>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {DOC_SLOTS.map(s => <Slot key={s.key} slot={s}/>)}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="font-body text-[11px] text-[#8A8A82]">Documents encrypted at rest · retained for full audit trail.</div>
          <button data-testid="claim-submit" disabled={!allGreen || loading} onClick={submit}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-3 rounded-sm inline-flex items-center gap-2 ${
                    allGreen && !loading
                      ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black"
                      : "bg-[#E5484D]/20 border border-[#E5484D]/50 text-[#E5484D] cursor-not-allowed"
                  }`}>
            {loading ? <Loader2 size={12} className="animate-spin"/>
                      : allGreen ? <FileCheck2 size={12}/> : <ShieldX size={12}/>}
            {allGreen ? (prefill?.has_prior ? "Resubmit iron-clad claim" : "Submit iron-clad claim") : "Compliance Violation"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
